import React, { useState, useCallback } from 'react';
import { Modal, Input, List, Avatar, Button, Tag, Space, message, Typography, Spin } from 'antd';
import { LinkOutlined, DisconnectOutlined, SearchOutlined } from '@ant-design/icons';
import {
  ElevenDbCandidate,
  ElevenDbSearchResult,
} from '../../service/api/elevenDbApiService';

const { Text } = Typography;

interface ElevenDbLinkingPanelProps {
  entityType: 'cast' | 'series' | 'content';
  entityId: number;
  entityName: string;
  entityNameKo?: string;
  entityNameEn?: string;
  currentElevenDbId: number | null;
  onSearch: (query: string) => Promise<ElevenDbSearchResult>;
  onLink: (elevenDbId: number) => Promise<void>;
  onUnlink: () => Promise<void>;
}

const ElevenDbLinkingPanel: React.FC<ElevenDbLinkingPanelProps> = ({
  entityType,
  entityName,
  entityNameKo,
  entityNameEn,
  currentElevenDbId,
  onSearch,
  onLink,
  onUnlink,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [candidates, setCandidates] = useState<ElevenDbCandidate[]>([]);
  const [loading, setLoading] = useState(false);
  const [linking, setLinking] = useState(false);

  const handleSearch = useCallback(
    async (query: string) => {
      if (!query.trim()) return;
      setLoading(true);
      try {
        const result = await onSearch(query);
        setCandidates(result.results || []);
      } catch (error) {
        message.error('11DB search failed');
      } finally {
        setLoading(false);
      }
    },
    [onSearch]
  );

  const handleLink = useCallback(
    async (candidate: ElevenDbCandidate) => {
      setLinking(true);
      try {
        await onLink(candidate.id);
        message.success(`Linked to 11DB: ${candidate.name || candidate.title}`);
        setIsModalOpen(false);
        setCandidates([]);
        setSearchQuery('');
      } catch (error) {
        message.error('Failed to link');
      } finally {
        setLinking(false);
      }
    },
    [onLink]
  );

  const handleUnlink = useCallback(async () => {
    try {
      await onUnlink();
      message.success('Unlinked from 11DB');
    } catch (error) {
      message.error('Failed to unlink');
    }
  }, [onUnlink]);

  const openModal = () => {
    setIsModalOpen(true);
    setSearchQuery(entityName);
    handleSearch(entityName);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }}>
        <Space>
          <Text strong>11DB Link:</Text>
          {currentElevenDbId ? (
            <>
              <a
                href={`https://www.11db.io/${entityType === 'cast' ? 'people' : entityType === 'series' ? 'tvshows' : 'movies'}/details/${currentElevenDbId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Tag color="green" style={{ cursor: 'pointer' }}>Linked (ID: {currentElevenDbId})</Tag>
              </a>
              <Button
                size="small"
                icon={<DisconnectOutlined />}
                onClick={handleUnlink}
                danger
              >
                Unlink
              </Button>
            </>
          ) : (
            <Tag color="default">Not linked</Tag>
          )}
          <Button size="small" type="primary" icon={<SearchOutlined />} onClick={openModal}>
            Search 11DB
          </Button>
        </Space>
      </Space>

      <Modal
        title={`Link ${entityType} to 11DB`}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={700}
      >
        {(entityNameKo || entityNameEn) && (
          <Space style={{ marginBottom: 8 }}>
            {entityNameKo && (
              <Button
                size="small"
                onClick={() => {
                  setSearchQuery(entityNameKo);
                  handleSearch(entityNameKo);
                }}
              >
                한글: {entityNameKo}
              </Button>
            )}
            {entityNameEn && (
              <Button
                size="small"
                onClick={() => {
                  setSearchQuery(entityNameEn);
                  handleSearch(entityNameEn);
                }}
              >
                영문: {entityNameEn}
              </Button>
            )}
          </Space>
        )}
        <Input.Search
          placeholder="Search by name..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onSearch={handleSearch}
          enterButton
          style={{ marginBottom: 16 }}
        />
        <Spin spinning={loading}>
          <List
            dataSource={candidates}
            renderItem={candidate => (
              <List.Item
                actions={[
                  <Button
                    type="primary"
                    size="small"
                    icon={<LinkOutlined />}
                    onClick={() => handleLink(candidate)}
                    loading={linking}
                  >
                    Link
                  </Button>,
                ]}
              >
                <List.Item.Meta
                  avatar={
                    candidate.poster ? (
                      <Avatar src={candidate.poster} size={48} shape="square" />
                    ) : (
                      <Avatar size={48} shape="square">
                        {(candidate.name || candidate.title || '?')[0]}
                      </Avatar>
                    )
                  }
                  title={
                    <a
                      href={`https://www.11db.io/${entityType === 'cast' ? 'people' : entityType === 'series' ? 'tvshows' : 'movies'}/details/${candidate.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {candidate.name || candidate.title}
                    </a>
                  }
                  description={
                    <Space direction="vertical" size={2} style={{ width: '100%' }}>
                      <Space wrap>
                        {candidate.birth_date && (
                          <Text type="secondary">Born: {candidate.birth_date}</Text>
                        )}
                        {candidate.gender && (
                          <Text type="secondary">Gender: {candidate.gender}</Text>
                        )}
                        {candidate.year && <Text type="secondary">Year: {candidate.year}</Text>}
                        {candidate.type && <Tag>{candidate.type}</Tag>}
                        <Text type="secondary">11DB ID: {candidate.id}</Text>
                      </Space>
                      {candidate.known_for && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Known for: {candidate.known_for}
                        </Text>
                      )}
                    </Space>
                  }
                />
              </List.Item>
            )}
            locale={{ emptyText: loading ? 'Searching...' : 'No results' }}
          />
        </Spin>
      </Modal>
    </div>
  );
};

export default ElevenDbLinkingPanel;
